// labyrinth-suggest-cell.ts — one Labyrinth suggestion-queue row, as a
// keyed-list Cell. Pure: it owns only its DOM + the two action callbacks, so it
// renders identically to the old inline builder in labyrinth-view.ts and can be
// unit-tested without the (CM6-bound) view module. The cell key is the
// suggestion's stable key (`oversize:…`), so the reconciler reuses the row's
// node across re-renders for the same suggestion.

import { cardActions } from '../ui/card-panel.js';
import type { Cell } from '../ui/keyed-list.js';
import { escapeHtml } from '../utils/sanitize.js';
import type { Suggestion } from './note-graph-index.js';

/**
 * Build the keyed-list Cell for one suggestion row.
 *
 * @param s         the suggestion (its `.key` becomes the cell key)
 * @param onAccept  invoked when the accept button (Split) is clicked
 * @param onDecline invoked when the decline button (Keep) is clicked
 *
 * Button labels are fixed (the only suggestion type is 'oversize') and `s.key`
 * (`oversize:<nbId>`) identifies the row, so the handlers wired at mount stay
 * correct for the life of the node; `update` only refreshes the reason text.
 */
export function suggestionCell(s: Suggestion, onAccept: () => void, onDecline: () => void): Cell {
  const accept = 'Split';
  const decline = 'Keep';
  return {
    key: s.key,
    mount() {
      const item = document.createElement('div');
      item.className = 'labyrinth-suggest-item';
      item.innerHTML =
        `<div class="labyrinth-suggest-reason">${escapeHtml(s.reason)}</div>` +
        cardActions(
          `<button class="labyrinth-suggest-accept" type="button">${accept}</button>` +
            `<button class="labyrinth-suggest-decline" type="button">${decline}</button>`
        );
      item
        .querySelector<HTMLButtonElement>('.labyrinth-suggest-accept')
        ?.addEventListener('click', onAccept);
      item
        .querySelector<HTMLButtonElement>('.labyrinth-suggest-decline')
        ?.addEventListener('click', onDecline);
      return item;
    },
    update(item) {
      const reasonEl = item.querySelector('.labyrinth-suggest-reason');
      if (reasonEl) reasonEl.textContent = s.reason;
    },
  };
}
